import {
  findingTitleEmphasis,
  isPerformanceCheckId,
  PERFORMANCE_USAGE_CHECK_ID,
  performanceIssueHeaderCopy,
  splitEmphasizedCopy,
  type QuestionQualityCheck,
  type QuestionQualityHistory,
} from "@/lib/question-quality"
import { cn } from "@/lib/utils"

export function FindingCopyEmphasis({
  text,
  phrases,
}: {
  text: string
  phrases: string[]
}) {
  return (
    <>
      {splitEmphasizedCopy(text, phrases).map((part, index) =>
        part.emphasize ? (
          <strong
            key={`${part.text}-${index}`}
            className="font-semibold italic"
          >
            {part.text}
          </strong>
        ) : (
          <span key={`${part.text}-${index}`}>{part.text}</span>
        ),
      )}
    </>
  )
}

export function FindingTitleEmphasis({
  check,
}: {
  check: Pick<QuestionQualityCheck, "title" | "markText" | "currentText">
}) {
  return (
    <FindingCopyEmphasis
      text={check.title}
      phrases={findingTitleEmphasis(check)}
    />
  )
}

/** Pager header for exam-history rows: lead + inline stat + consequence. */
export function PerformanceIssueHeader({
  check,
  history,
}: {
  check: QuestionQualityCheck
  history: QuestionQualityHistory
}) {
  const copy = performanceIssueHeaderCopy(check, history)
  const reuse = check.id === PERFORMANCE_USAGE_CHECK_ID

  if (!copy) {
    return (
      <p className="m-0 min-w-0 text-sm font-normal leading-snug tracking-tight text-foreground">
        {check.title}
      </p>
    )
  }

  return (
    <p className="m-0 min-w-0 text-sm font-normal leading-snug tracking-tight text-foreground">
      {copy.before}
      <span
        className={cn(
          "mx-0.5 inline rounded-md px-1.5 py-0.5 font-mono text-xs tabular-nums leading-snug",
          reuse
            ? "bg-destructive/10 font-semibold text-destructive"
            : "bg-muted font-medium text-foreground",
        )}
      >
        {copy.highlight}
      </span>
      {copy.after}
    </p>
  )
}

export function LeoPagerFindingLead({
  check,
  history = null,
}: {
  check: QuestionQualityCheck
  history?: QuestionQualityHistory | null
}) {
  if (history?.sat && isPerformanceCheckId(check.id)) {
    return <PerformanceIssueHeader check={check} history={history} />
  }
  return <FindingTitleEmphasis check={check} />
}
